import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Route, Calendar, MapPin, Check } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { useAuthStore, type ActivePerformance } from '@/lib/auth-store'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'

interface Tour {
  id: string
  name: string
  description: string | null
  status: string
  startDate: string | null
  endDate: string | null
  memberCount: number
  legCount: number
}

interface Show {
  id: string
  title: string
  date: string
  venue: string
  city: string
  country: string
  capacityTotal: number
  capacitySold: number
  stage: string
  tourId?: string | null
}

function formatDate(iso?: string | null): string | null {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
    })
  } catch {
    return null
  }
}

function formatRange(start?: string | null, end?: string | null): string {
  const s = formatDate(start)
  const e = formatDate(end)
  if (s && e) return `${s} – ${e}`
  if (s) return `From ${s}`
  if (e) return `Through ${e}`
  return '—'
}

export function PerformancesPage() {
  const navigate = useNavigate()
  const active = useAuthStore((s) => s.activePerformance)
  const setActive = useAuthStore((s) => s.setActivePerformance)

  const toursQ = useQuery<Tour[], ApiClientError>({
    queryKey: ['perf-page-tours'],
    queryFn: () => artistFetch<Tour[]>('/tours'),
  })
  const showsQ = useQuery<Show[], ApiClientError>({
    queryKey: ['perf-page-shows'],
    queryFn: () => artistFetch<Show[]>('/shows'),
  })

  const tours = toursQ.data ?? []
  const allShows = showsQ.data ?? []
  // Standalone = shows not part of a tour. The /shows endpoint may or may
  // not expose tourId; if absent we treat the show as standalone.
  const standaloneShows = useMemo(
    () => allShows.filter((s) => !s.tourId),
    [allShows],
  )

  const upcomingTours = useMemo(
    () =>
      [...tours].sort((a, b) =>
        (a.startDate ?? '').localeCompare(b.startDate ?? ''),
      ),
    [tours],
  )
  const upcomingShows = useMemo(
    () =>
      [...standaloneShows].sort((a, b) =>
        (a.date ?? '').localeCompare(b.date ?? ''),
      ),
    [standaloneShows],
  )

  function pickTour(t: Tour) {
    const p: ActivePerformance = { kind: 'tour', id: t.id, name: t.name }
    setActive(p)
  }
  function pickShow(s: Show) {
    const p: ActivePerformance = {
      kind: 'show',
      id: s.id,
      name: s.title || `${s.venue || 'Untitled'} · ${s.city || ''}`.trim(),
    }
    setActive(p)
  }

  const isActiveTour = (id: string) => active?.kind === 'tour' && active.id === id
  const isActiveShow = (id: string) => active?.kind === 'show' && active.id === id

  const isLoading = toursQ.isLoading || showsQ.isLoading
  const error = toursQ.error ?? showsQ.error

  return (
    <div>
      <PageHeader
        title="Performances"
        subtitle="Tours and standalone shows for this artist. Selecting one scopes data across the performance workflow pages."
        actions={
          active && (
            <button
              type="button"
              onClick={() => setActive(null)}
              className="rounded-md border border-ovation-ink/15 bg-white px-3 py-1.5 text-xs font-medium text-ovation-ink hover:bg-ovation-paper"
            >
              Clear selection
            </button>
          )
        }
      />

      <AsyncBoundary isLoading={isLoading} error={error}>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Tours */}
          <section className="rounded-lg border border-ovation-ink/10 bg-white p-5">
            <h2 className="mb-4 flex items-center gap-2 text-base font-semibold text-ovation-ink">
              <Route className="h-4 w-4" />
              Tours
              <span className="text-xs font-normal text-ovation-muted">({upcomingTours.length})</span>
            </h2>
            {upcomingTours.length === 0 ? (
              <p className="text-sm text-ovation-muted">No tours yet.</p>
            ) : (
              <ul className="divide-y divide-ovation-ink/5">
                {upcomingTours.map((t) => (
                  <li key={t.id} className="py-3">
                    <div className="flex items-start justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => pickTour(t)}
                        className="flex-1 text-left"
                      >
                        <div className="font-medium text-ovation-ink">{t.name}</div>
                        <div className="mt-0.5 text-xs text-ovation-muted">
                          {formatRange(t.startDate, t.endDate)}
                          {t.legCount > 0 && (
                            <> · {t.legCount} leg{t.legCount === 1 ? '' : 's'}</>
                          )}
                          {t.memberCount > 0 && (
                            <> · {t.memberCount} member{t.memberCount === 1 ? '' : 's'}</>
                          )}
                          {t.status && (
                            <> · <span className="capitalize">{t.status.replace(/_/g, ' ')}</span></>
                          )}
                        </div>
                      </button>
                      <div className="flex items-center gap-1.5">
                        {isActiveTour(t.id) && (
                          <span className="flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-[10px] uppercase tracking-wider text-emerald-800">
                            <Check className="h-3 w-3" />
                            Active
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => navigate(`/tours/${t.id}`)}
                          className="rounded-md border border-ovation-ink/15 bg-white px-2 py-1 text-xs font-medium text-ovation-ink hover:bg-ovation-paper"
                        >
                          Open
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Standalone shows */}
          <section className="rounded-lg border border-ovation-ink/10 bg-white p-5">
            <h2 className="mb-4 flex items-center gap-2 text-base font-semibold text-ovation-ink">
              <MapPin className="h-4 w-4" />
              Standalone shows
              <span className="text-xs font-normal text-ovation-muted">({upcomingShows.length})</span>
            </h2>
            {upcomingShows.length === 0 ? (
              <p className="text-sm text-ovation-muted">No standalone shows.</p>
            ) : (
              <ul className="divide-y divide-ovation-ink/5">
                {upcomingShows.map((s) => (
                  <li key={s.id} className="py-3">
                    <div className="flex items-start justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => pickShow(s)}
                        className="flex-1 text-left"
                      >
                        <div className="font-medium text-ovation-ink">
                          {s.title || s.venue || 'Untitled show'}
                        </div>
                        <div className="mt-0.5 text-xs text-ovation-muted">
                          <Calendar className="mr-1 inline h-3 w-3" />
                          {formatDate(s.date) ?? '—'}
                          {(s.city || s.country) && (
                            <> · {[s.city, s.country].filter(Boolean).join(', ')}</>
                          )}
                          {s.venue && <> · {s.venue}</>}
                          {s.stage && (
                            <> · <span className="capitalize">{s.stage.replace(/_/g, ' ')}</span></>
                          )}
                        </div>
                      </button>
                      <div className="flex items-center gap-1.5">
                        {isActiveShow(s.id) && (
                          <span className="flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-[10px] uppercase tracking-wider text-emerald-800">
                            <Check className="h-3 w-3" />
                            Active
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => navigate(`/shows/${s.id}`)}
                          className="rounded-md border border-ovation-ink/15 bg-white px-2 py-1 text-xs font-medium text-ovation-ink hover:bg-ovation-paper"
                        >
                          Open
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </AsyncBoundary>
    </div>
  )
}
